/**
 * PromptService - System Prompt Management with Version Control
 * 
 * Manages all AI system prompts with automatic versioning and context awareness
 * Enhanced in v5.1.1 with version control and metadata support
 * 
 * @module services/PromptService
 */

import AppState from '../core/AppState.js';
import EventBus, { AppEvents } from '../core/EventBus.js';
import Config from '../core/Config.js';
import { validatePrompt } from '../utils/schema.js';
import StorageService from '../services/StorageService.js';

/**
 * PromptVersion class for version control
 */
class PromptVersion {
  constructor(systemRole, metadata = {}) {
    this.systemRole = systemRole;
    this.version = this.generateVersion();
    this.createdAt = new Date().toISOString();
    this.author = metadata.author || 'user';
    this.hash = this.generateHash(systemRole);
    this.changeNote = metadata.changeNote || 'Auto-generated version';
  }
  
  generateVersion() {
    const now = new Date();
    return `${now.getFullYear()}.${(now.getMonth() + 1).toString().padStart(2, '0')}.${now.getDate().toString().padStart(2, '0')}.${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}`;
  }
  
  generateHash(content) {
    // Simple hash for change detection
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(16);
  }
}

class PromptService {
  constructor() {
    this.initialized = false;
    this.storageKey = 'promptServiceData';
    this.conversationDepth = 0;
    
    // Core system prompts with version control
    this.prompts = {
      strategyFileUpload: {
        name: 'Strategy File Upload',
        description: 'Extracts strategy from uploaded documents',
        currentVersion: '2024.12.31.1200',
        versions: {
          '2024.12.31.1200': new PromptVersion('You are an expert at extracting strategic frameworks from documents. Focus on identifying strategic pillars, objectives, and key themes.', {
            author: 'system',
            changeNote: 'Initial version with version control'
          })
        },
        temperature: 0.5,
        maxTokens: 2000
      },
      
      outcomeAssistant: {
        name: 'Outcome Assistant',
        description: 'Transforms outputs into business outcomes',
        currentVersion: '2024.12.31.1200',
        versions: {
          '2024.12.31.1200': new PromptVersion(`You are a strategic planning expert focused on business outcomes over outputs.

Key Principles:
- Transform activities into measurable business value
- Focus on WHO benefits and HOW they benefit
- Emphasize outcomes (what changes) not outputs (what you produce)
- Make objectives ambitious yet achievable
- Ensure all metrics are specific, time-bound, and measurable

When helping users create OKRs:
1. Start with understanding the business problem or opportunity
2. Identify the stakeholders who will benefit
3. Define success in terms of value delivered, not activities completed
4. Create 1 clear objective and 2-4 measurable key results
5. Always ask: "What meaningful change will this create?"

Examples of good outcome focus:
- NOT: "Deploy new CRM system" BUT: "Reduce customer churn by 15% through improved engagement"
- NOT: "Train 100 employees" BUT: "Increase productivity by 25% through skill enhancement"
- NOT: "Launch mobile app" BUT: "Increase customer engagement by 40% via mobile channel"

Help transform any output-focused request into outcome-focused thinking.`, {
            author: 'system',
            changeNote: 'Initial version with version control'
          })
        },
        temperature: 0.7,
        maxTokens: 2000
      },
      
      initiativeSearch: {
        name: 'Initiative Search & Analysis',
        description: 'AI-powered semantic search with strategic insights',
        currentVersion: '2024.12.31.1200',
        versions: {
          '2024.12.31.1200': new PromptVersion(`You are an intelligent initiative database analyst. When given a search query and initiative database, provide smart search results and strategic insights.

SEARCH CAPABILITIES:
- Semantic similarity matching (not just keyword matching)
- Strategic theme analysis  
- Risk and opportunity identification
- Cross-initiative relationships
- Portfolio gap analysis

RESPONSE FORMAT:
Return results as JSON:
{
  "relevantInitiatives": [
    {
      "id": "initiative_id",
      "relevanceScore": 0.95,
      "matchReason": "Why this matches the search query",
      "initiative": {original initiative object}
    }
  ],
  "insights": [
    "Strategic insight 1",
    "Pattern recognition insight 2"
  ],
  "suggestions": [
    "Suggested action 1", 
    "Related search suggestion 2"
  ],
  "summary": "Executive summary of search results"
}

Focus on strategic value and business impact, not just text matching.`, {
            author: 'system',
            changeNote: 'Initial AI search prompt'
          })
        },
        temperature: 0.4,
        maxTokens: 2000
      },
      
      emergentStrategy: {
        name: 'Emergent Strategy',
        description: 'Generates emergent strategy from initiatives',
        currentVersion: '2024.12.31.1200',
        versions: {
          '2024.12.31.1200': new PromptVersion('You are a strategic pattern recognition expert. Identify emergent themes and strategic directions from bottom-up initiatives.', {
            author: 'system',
            changeNote: 'Initial version with version control'
          })
        },
        temperature: 0.6,
        maxTokens: 2000
      },
      
      riskAnalysis: {
        name: 'Risk Analysis',
        description: 'Strategic risk analysis for initiatives',
        currentVersion: '2024.12.31.1200',
        versions: {
          '2024.12.31.1200': new PromptVersion(`You are a strategic risk analyst focused on identifying specific, actionable risks for business initiatives.

Key Principles:
- Identify SPECIFIC risks unique to this initiative, not generic risks
- Focus on strategic, operational, and implementation risks
- Provide actionable mitigation strategies
- Consider stakeholder, resource, and timeline risks
- Assess both internal and external risk factors

Risk Analysis Framework:
1. Strategic Risks: Alignment issues, market changes, competitive threats
2. Operational Risks: Resource constraints, capability gaps, process failures
3. Implementation Risks: Timeline delays, scope creep, stakeholder resistance
4. External Risks: Regulatory changes, economic conditions, technology shifts
5. Mitigation Strategies: Specific actions to reduce risk probability or impact

Avoid generic risks like:
- "Communication issues" → Instead: "Misalignment between Sales and Product teams on feature priorities"
- "Budget overruns" → Instead: "Integration costs with legacy system may exceed budget by 30%"
- "Technical challenges" → Instead: "Data migration from Oracle to Salesforce requires 3-month timeline extension"

Provide specific, contextual, actionable risk insights.`, {
            author: 'system',
            changeNote: 'Initial version with version control'
          })
        },
        temperature: 0.4,
        maxTokens: 2000
      },
      
      portfolioAnalysis: {
        name: 'Portfolio Analysis',
        description: 'Conversational narrative about portfolio fit to strategy',
        currentVersion: '2025.01.01.0900',
        versions: {
          '2025.01.01.0900': new PromptVersion(`You are a strategic portfolio analyst. Produce a concise, conversational narrative (plain paragraphs, no headings or lists) explaining how the current portfolio fits the organization's uploaded strategy.

Requirements:
- Focus on how initiatives support each strategic pillar and where there are gaps or over/under‑coverage.
- Be specific to the provided portfolio and pillars; avoid generic advice.
- Do NOT include risks or recommendations as separate sections.
- Keep the tone executive and natural. Avoid templates, JSON, or bullet points.`, {
            author: 'system',
            changeNote: 'Switch to conversational narrative style (no sections/lists)'
          })
        },
        temperature: 0.5,
        maxTokens: 2000
      },
      
      transferStrategy: {
        name: 'Transfer Strategy',
        description: 'Transfers context between chats',
        currentVersion: '2024.12.31.1200',
        versions: {
          '2024.12.31.1200': new PromptVersion('You help transfer strategic context and initiatives between conversations to maintain continuity.', {
            author: 'system',
            changeNote: 'Initial version with version control'
          })
        },
        temperature: 0.5,
        maxTokens: 1000
      },
      
      initiativeExtractor: {
        name: 'Initiative Extractor',
        description: 'Extracts initiatives from conversations',
        currentVersion: '2024.12.31.1200',
        versions: {
          '2024.12.31.1200': new PromptVersion('You are an expert at extracting and structuring initiatives from conversations and documents.', {
            author: 'system',
            changeNote: 'Initial version with version control'
          })
        },
        temperature: 0.5,
        maxTokens: 2000
      },
      
      initiativeTransfer: {
        name: 'Initiative Transfer',
        description: 'Formats initiatives for transfer to forms',
        currentVersion: '2024.12.31.1200',
        versions: {
          '2024.12.31.1200': new PromptVersion(`When the user has a clear initiative idea, format it for transfer:

[TRANSFERABLE_INITIATIVE]
{
  "title": "Clear, outcome-focused title",
  "deliverable": "What will be built/done (feature, action, playbook) — optional",
  "objective": "What business outcome will be achieved",
  "keyResults": [
    "Specific, measurable result / impact 1",
    "Specific, measurable result / impact 2",
    "Specific, measurable result / impact 3"
  ],
  "strategicAlignment": "Which strategic pillar this supports",
  "stakeholders": ["Key stakeholder 1", "Key stakeholder 2"],
  "metrics": ["15% improvement", "$2M revenue", "30% reduction"]
}
[/TRANSFERABLE_INITIATIVE]

Only create transferable initiatives when the user has provided enough detail and context.`, {
            author: 'system',
            changeNote: 'Initial version with version control'
          })
        },
        temperature: 0.5,
        maxTokens: 1000
      },
      metricsAssistant: {
        name: 'Metrics Assistant',
        description: 'Explains status, metrics, progress, dependencies and risks using local data context',
        currentVersion: '2025.09.12.1200',
        versions: {
          '2025.09.12.1200': new PromptVersion(`You are a helpful strategy metrics analyst.

You receive a user question and a JSON CONTEXT block with local data (initiatives, counts, progress).
Respond with a concise, plain-language answer grounded in the CONTEXT. If data is missing, say so clearly.

Rules:
- Reference specific numbers from CONTEXT where relevant.
- Do not invent metrics not present in CONTEXT.
- If the user asks broadly, provide an executive summary with key callouts.
- Keep it short and readable; avoid tables unless asked.

If CONTEXT includes dependencies or risks:
- Highlight any blockers where dependent initiatives are at risk or delayed.
- Summarize risk hotspots (high/medium/low) and name the most critical items.
- Offer 1–2 practical next steps based on the data (optional).`, { author: 'system', changeNote: 'Initial metrics assistant with deps/risks' })
        },
        temperature: 0.3,
        maxTokens: 800
      },
      metricsIntent: {
        name: 'Metrics Intent',
        description: 'Extracts intent for metrics/status queries in strict JSON',
        currentVersion: '2025.09.12.1200',
        versions: {
          '2025.09.12.1200': new PromptVersion(`You analyze a user's question about status/progress/metrics and return a STRICT JSON intent describing what to compute locally.

Rules:
- Output only a single JSON object, no prose, no explanations.
- If needed, infer sensible defaults; avoid asking clarifying questions.
- Use these fields:
  {
    "scope": "portfolio" | "initiative" | "objective" | "kr",
    "selection": { "ids": [string], "titles": [string], "owners": [string] },
    "timeRange": { "from": string | null, "to": string | null },
    "metrics": ["progress","target_delta","confidence"],
    "enrich": { "includeDependencies": boolean, "includeRisks": boolean, "includeLinks": boolean },
    "aggregate": { "method": "avg" | "weighted" | "sum", "groupBy": "owner" | "type" | null }
  }

Defaults:
- scope: "portfolio"
- metrics: ["progress"]
- enrich: all false
- aggregate: { method: "avg", groupBy: null }
`, { author: 'system', changeNote: 'Initial metrics intent prompt'})
        },
        temperature: 0.0,
        maxTokens: 300
      }
    };
    
    // Load any saved custom prompts
    this.loadCustomPrompts();
  }
  
  /**
   * Initialize the service
   */
  async initialize() {
    console.log('🎯 Initializing PromptService...');
    // Optional: load shared prompts from remote JSON (pilot/distributed defaults)
    try {
      const url = Config?.REMOTE_PROMPTS_URL;
      if (url && typeof fetch === 'function') {
        const res = await fetch(url, { cache: 'no-cache' });
        if (res.ok) {
          const remote = await res.json();
          if (remote && typeof remote === 'object') {
            // Merge: only known prompts; keep structure/versioning minimal
            Object.keys(remote).forEach((key) => {
              const data = remote[key] || {};
              if (!this.prompts[key]) return;
              const sys = String(data.systemRole || '');
              const temp = typeof data.temperature === 'number' ? data.temperature : this.prompts[key].temperature;
              if (sys) {
                this.prompts[key].currentVersion = 'remote-1.0.0';
                this.prompts[key].versions = {
                  'remote-1.0.0': new PromptVersion(sys, { author: 'remote', changeNote: 'Remote prompts' })
                };
                this.prompts[key].temperature = temp;
                this.prompts[key].customized = true;
              }
            });
            console.log('🌐 Loaded remote prompts from', url);
          }
        }
      }
    } catch (e) {
      console.warn('Remote prompts load failed:', e?.message || e);
    }
    
    // Ensure all default prompts are properly initialized
    Object.keys(this.prompts).forEach(key => {
      const prompt = this.prompts[key];
      // Ensure versions structure exists
      if (!prompt.versions && prompt.systemRole) {
        prompt.currentVersion = '1.0.0';
        prompt.versions = {
          '1.0.0': {
            systemRole: prompt.systemRole,
            version: '1.0.0',
            createdAt: new Date().toISOString(),
            author: 'system',
            changeNote: 'Auto-generated version'
          }
        };
      }
    });
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Subscribe to conversation depth changes
    AppState.subscribe('chat.conversationDepth', (depth) => {
      this.conversationDepth = depth;
    });
    
    this.initialized = true;
    console.log('✅ PromptService initialized with', Object.keys(this.prompts).length, 'prompts');
  }
  
  /**
   * Set up event listeners
   */
  setupEventListeners() {
    // Listen for prompt update events
    EventBus.on('prompt:update', ({ key, updates }) => {
      this.updatePrompt(key, updates);
    });
    
    // Listen for conversation events
    EventBus.on(AppEvents.CHAT_MESSAGE_SENT, () => {
      this.conversationDepth++;
    });
    
    EventBus.on(AppEvents.CHAT_CLEARED, () => {
      this.conversationDepth = 0;
    });
  }
  
  /**
   * Get prompt for specific context with version support
   * @param {string} promptKey - The prompt identifier
   * @param {Object} context - Additional context for the prompt
   * @returns {Object} Prompt configuration
   */
  getPrompt(promptKey, context = {}) {
    // First check namespaced storage for custom prompts (preferred)
    try {
      const customPromptsSip = StorageService.get('customPrompts', null);
      if (customPromptsSip && customPromptsSip[promptKey]) {
        return {
          ...customPromptsSip[promptKey],
          name: this.prompts[promptKey]?.name || promptKey,
          description: this.prompts[promptKey]?.description || 'Custom prompt'
        };
      }
    } catch (e) {
      console.warn('Error loading custom prompts from StorageService:', e);
    }
    
    // Backward compatibility: also check raw localStorage key
    try {
      const customPrompts = JSON.parse(localStorage.getItem('customPrompts') || '{}');
      if (customPrompts[promptKey]) {
        return {
          ...customPrompts[promptKey],
          name: this.prompts[promptKey]?.name || promptKey,
          description: this.prompts[promptKey]?.description || 'Custom prompt'
        };
      }
    } catch (e) {
      console.warn('Error loading legacy custom prompts:', e);
    }
    
    const basePrompt = this.prompts[promptKey];
    if (!basePrompt) {
      console.warn(`Prompt not found: ${promptKey}`);
      return this.getDefaultPrompt(promptKey);
    }
    
    // Get current version safely
    const currentVersion = this.getCurrentVersion(promptKey);
    if (!currentVersion) {
      console.warn(`No current version found for prompt: ${promptKey}`);
      return this.getDefaultPrompt(promptKey);
    }
    
    // Clone the prompt to avoid mutations
    const prompt = { 
      ...basePrompt,
      systemRole: currentVersion.systemRole
    };
    
    // Apply context-aware modifications
    if (promptKey === 'outcomeAssistant') {
      // Personal, impact-first tone + early conversation guidance
      prompt.systemRole += `\n\nTONE & STYLE:\n- Be a warm, personal strategic partner. Use first person.\n- Keep responses concise and actionable.\n- Encourage thinking about people and impact (end users, customers, teams).\n\nEXTRACTION:\n- When the user provides substance, infer and surface:\n  { objective, description, key_results[], stakeholders[], owner_guess, timeframe_guess }\n- Confirm stakeholders (actors/end users) with the user.\n\nEARLY DIALOGUE (#${this.conversationDepth + 1}):\n- Ask 1 short clarifying question if needed to understand the outcome and who benefits.\n- Avoid listing many initiatives; focus the conversation on outcomes first.`;
    }
    
    // Apply any context overrides
    if (context.temperature !== undefined) {
      prompt.temperature = context.temperature;
    }
    if (context.maxTokens !== undefined) {
      prompt.maxTokens = context.maxTokens;
    }
    
    return prompt;
  }
  
  /**
   * Get default prompt for fallback or reset
   * @param {string} promptKey - Optional specific prompt to get default for
   */
  getDefaultPrompt(promptKey = null) {
    // If a specific key is requested, return the original default for that prompt
    if (promptKey) {
      // Store original defaults
      const defaults = {
        strategyFileUpload: {
          name: 'Strategy File Upload',
          description: 'Extracts strategy from uploaded documents',
          systemRole: 'You are an expert at extracting strategic frameworks from documents. Focus on identifying strategic pillars, objectives, and key themes.',
          temperature: 0.5,
          maxTokens: 2000
        },
        outcomeAssistant: {
          name: 'Outcome Assistant',
          description: 'Transforms outputs into business outcomes',
          systemRole: `You are an expert strategic business consultant focused on outcomes over outputs.
Your role is to help users articulate clear, measurable business objectives that create real value.

Key Principles:
- Transform activities into measurable business value
- Focus on WHO benefits and HOW they benefit
- Emphasize outcomes (what changes) not outputs (what you produce)
- Make objectives ambitious yet achievable
- Ensure all metrics are specific, time-bound, and measurable

When helping users create OKRs:
1. Start with understanding the business problem or opportunity
2. Identify the stakeholders who will benefit
3. Define success in terms of value delivered, not activities completed
4. Create 1 clear objective and 2-4 measurable key results
5. Always ask: "What meaningful change will this create?"

Examples of good outcome focus:
- NOT: "Deploy new CRM system" BUT: "Reduce customer churn by 15% through improved engagement"
- NOT: "Train 100 employees" BUT: "Increase productivity by 25% through skill enhancement"
- NOT: "Launch mobile app" BUT: "Increase customer engagement by 40% via mobile channel"

Help transform any output-focused request into outcome-focused thinking.`,
          temperature: 0.7,
          maxTokens: 2000
        },
        emergentStrategy: {
          name: 'Emergent Strategy',
          description: 'Generates emergent strategy from initiatives',
          systemRole: 'You are a strategic pattern recognition expert. Identify emergent themes and strategic directions from bottom-up initiatives.',
          temperature: 0.6,
          maxTokens: 2000
        }
      };
      
      return defaults[promptKey] || this.getDefaultPrompt();
    }
    
    // Return generic default
    return {
      name: 'Default Assistant',
      description: 'General AI assistant',
      systemRole: 'You are a helpful AI assistant focused on providing clear, actionable advice.',
      temperature: 0.7,
      maxTokens: 2000
    };
  }
  
  /**
   * Build complete messages array for API call
   * @param {string} userMessage - The user's message
   * @param {string} promptKey - Which prompt to use
   * @param {Array} history - Conversation history
   * @returns {Array} Messages array for API
   */
  buildMessages(userMessage, promptKey = 'outcomeAssistant', history = []) {
    const prompt = this.getPrompt(promptKey);
    
    // Build messages array
    const messages = [
      {
        role: 'system',
        content: prompt.systemRole
      }
    ];
    
    // Add conversation history
    if (history && history.length > 0) {
      messages.push(...history);
    }
    
    // Add user message
    messages.push({
      role: 'user',
      content: userMessage
    });
    
    return messages;
  }
  
  /**
   * Get prompt configuration for API call with version info
   * @param {string} promptKey - Which prompt to use
   * @returns {Object} API configuration
   */
  getApiConfig(promptKey = 'outcomeAssistant') {
    const prompt = this.getPrompt(promptKey);
    const basePrompt = this.prompts[promptKey];
    
    return {
      temperature: prompt.temperature,
      maxTokens: prompt.maxTokens,
      promptName: prompt.name,
      promptKey: promptKey,
      promptVersion: basePrompt?.currentVersion || '1.0.0',
      systemRole: prompt.systemRole
    };
  }
  
  /**
   * Extract transferable initiatives from response
   * @param {string} response - AI response text
   * @returns {Array} Extracted initiatives
   */
  extractTransferableInitiatives(response) {
    const regex = /\[TRANSFERABLE_INITIATIVE]([\s\S]*?)\[\/TRANSFERABLE_INITIATIVE]/g;
    const initiatives = [];
    let match;
    
    while ((match = regex.exec(response)) !== null) {
      try {
        const initiativeData = JSON.parse(match[1].trim());
        initiatives.push(initiativeData);
      } catch (e) {
        console.warn('Failed to parse initiative:', e);
      }
    }
    
    return initiatives;
  }
  
  /**
   * Check if response should trigger initiative transfer
   * @param {string} response - AI response
   * @returns {boolean}
   */
  shouldOfferTransfer(response) {
    // Only offer transfer if conversation is mature enough
    if (this.conversationDepth < 3) return false;
    
    // Check for initiative markers
    return response.includes('[TRANSFERABLE_INITIATIVE]') ||
           (response.includes('Objective:') && response.includes('Key Results:'));
  }
  
  /**
   * Get current version of a prompt
   * @param {string} promptKey - The prompt identifier
   * @returns {Object|null} Current version object
   */
  getCurrentVersion(promptKey) {
    const prompt = this.prompts[promptKey];
    if (!prompt) {
      console.warn(`Prompt not found: ${promptKey}, returning default`);
      // Return a default structure to prevent errors
      return {
        systemRole: 'You are a helpful AI assistant.',
        version: '1.0.0',
        createdAt: new Date().toISOString(),
        author: 'system',
        hash: 'default',
        changeNote: 'Default fallback prompt'
      };
    }
    
    // Check if versions exist
    if (!prompt.versions || !prompt.currentVersion) {
      // Create a default version from the prompt data
      return {
        systemRole: prompt.systemRole || 'You are a helpful AI assistant.',
        version: '1.0.0',
        createdAt: new Date().toISOString(),
        author: 'system',
        hash: 'default',
        changeNote: 'Auto-generated version'
      };
    }
    
    return prompt.versions[prompt.currentVersion] || {
      systemRole: prompt.systemRole || 'You are a helpful AI assistant.',
      version: '1.0.0',
      createdAt: new Date().toISOString()
    };
  }
  
  /**
   * Get version history for a prompt
   * @param {string} promptKey - The prompt identifier
   * @returns {Array} Version history sorted by date
   */
  getVersionHistory(promptKey) {
    const prompt = this.prompts[promptKey];
    if (!prompt) return [];
    
    return Object.values(prompt.versions).sort((a, b) => 
      new Date(b.createdAt) - new Date(a.createdAt)
    );
  }
  
  /**
   * Update a prompt and auto-create new version
   * @param {string} key - Prompt key
   * @param {Object} updates - Updates to apply
   */
  updatePrompt(key, updates) {
    if (!this.prompts[key]) {
      console.warn(`Prompt ${key} doesn't exist, creating new one`);
      // Create a new prompt entry
      this.prompts[key] = {
        name: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
        description: 'Custom prompt',
        systemRole: updates.systemRole || '',
        temperature: updates.temperature || 0.7,
        customized: true,
        currentVersion: '1.0.0',
        versions: {
          '1.0.0': new PromptVersion(updates.systemRole || '', {
            author: updates.author || 'user',
            changeNote: 'Initial custom prompt'
          })
        }
      };
    }
    
    const currentPrompt = this.prompts[key];
    const currentVersion = currentPrompt.versions[currentPrompt.currentVersion];
    
    // Create new version if systemRole changed
    if (updates.systemRole && updates.systemRole !== currentVersion.systemRole) {
      const newVersion = new PromptVersion(updates.systemRole, {
        author: updates.author || 'user',
        changeNote: updates.changeNote || 'Prompt updated'
      });
      
      // Add new version
      currentPrompt.versions[newVersion.version] = newVersion;
      currentPrompt.currentVersion = newVersion.version;
      // Keep root prompt text in sync with active version for runtime usage
      currentPrompt.systemRole = newVersion.systemRole;
      
      console.log(`📝 Created new prompt version: ${key} v${newVersion.version}`);
    }
    
    // Update other properties
    const { systemRole, author, changeNote, ...otherUpdates } = updates;
    // Coerce/validate prompt shape
    if (typeof otherUpdates.temperature === 'string') {
      otherUpdates.temperature = parseFloat(otherUpdates.temperature);
    }
    if (typeof otherUpdates.temperature !== 'number' || isNaN(otherUpdates.temperature)) {
      otherUpdates.temperature = currentPrompt.temperature || 0.7;
    }
    const merged = { ...currentPrompt, ...otherUpdates };
    const check = validatePrompt({ systemRole: merged.systemRole || currentPrompt.systemRole, temperature: merged.temperature });
    if (!check.ok) {
      console.warn('Prompt validation failed; applying safe defaults');
      merged.temperature = Math.min(1, Math.max(0, merged.temperature || 0.7));
    }
    // If explicit systemRole provided without creating a new version (edge cases), sync it
    if (updates.systemRole && !currentPrompt.systemRole) {
      currentPrompt.systemRole = updates.systemRole;
    }

    Object.assign(currentPrompt, merged, {
      lastModified: new Date().toISOString(),
      customized: true
    });
    
    this.saveCustomPrompts();
    EventBus.emit('prompt:updated', { key, prompt: currentPrompt });
  }
  
  /**
   * Load custom prompts from storage
   */
  loadCustomPrompts() {
    try {
      // First try the service storage key (namespaced)
      let blob = StorageService.get(this.storageKey, null);
      if (!blob) {
        // Backward compatibility: raw localStorage
        const saved = localStorage.getItem(this.storageKey);
        blob = saved ? JSON.parse(saved) : null;
      }
      if (blob) {
        const data = blob;
        // Merge custom prompts with defaults
        Object.keys(data.prompts || {}).forEach(key => {
          if (data.prompts[key].customized) {
            // Ensure the prompt exists before overwriting
            if (!this.prompts[key]) {
              this.prompts[key] = {};
            }
            // Merge properties
            Object.assign(this.prompts[key], data.prompts[key]);
          }
        });
      }
      
      // Also load from namespaced customPrompts storage (preferred)
      let custom = StorageService.get('customPrompts', null);
      if (!custom) {
        // Backward compatibility: load legacy key
        const legacy = localStorage.getItem('customPrompts');
        custom = legacy ? JSON.parse(legacy) : null;
      }
      if (custom) {
        Object.keys(custom).forEach(key => {
          if (!this.prompts[key]) {
            // Create a new prompt entry if it doesn't exist
            this.prompts[key] = {
              name: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
              description: 'Custom prompt',
              systemRole: custom[key].systemRole || '',
              temperature: custom[key].temperature || 0.7,
              customized: true,
              currentVersion: '1.0.0',
              versions: {
                '1.0.0': {
                  systemRole: custom[key].systemRole || '',
                  version: '1.0.0',
                  createdAt: custom[key].lastModified || new Date().toISOString(),
                  author: 'user',
                  changeNote: 'Custom prompt'
                }
              }
            };
          } else if (custom[key].customized) {
            // Update existing prompt with custom values
            const t = typeof custom[key].temperature === 'string' ? parseFloat(custom[key].temperature) : custom[key].temperature;
            const shape = { systemRole: custom[key].systemRole, temperature: t };
            const ok = validatePrompt(shape).ok;
            if (ok) {
              this.prompts[key].systemRole = shape.systemRole;
              this.prompts[key].temperature = shape.temperature;
              this.prompts[key].customized = true;
            }
          }
        });
      }
      
      console.log('📚 Loaded custom prompts');
    } catch (error) {
      console.error('Failed to load custom prompts:', error);
    }
  }
  
  /**
   * Save custom prompts to storage
   */
  saveCustomPrompts() {
    try {
      // Persist only minimal custom data to avoid quota issues
      const minimal = {};
      Object.keys(this.prompts).forEach(key => {
        const p = this.prompts[key];
        if (p && p.customized) {
          minimal[key] = {
            systemRole: p.systemRole || '',
            temperature: typeof p.temperature === 'number' ? p.temperature : 0.7,
            customized: true,
            lastModified: p.lastModified || new Date().toISOString()
          };
        }
      });

      // Save using StorageService (compression + quota handling)
      StorageService.set('customPrompts', minimal);
      StorageService.set(this.storageKey, {
        prompts: minimal,
        version: '5.2.x',
        lastSaved: new Date().toISOString()
      });
      // Legacy compatibility for older builds
      try { localStorage.setItem('customPrompts', JSON.stringify(minimal)); } catch {}
    } catch (error) {
      console.error('Failed to save custom prompts:', error);
    }
  }
  
  /**
   * Format a prompt with variables
   * @param {string} promptKey - The prompt identifier
   * @param {Object} variables - Variables to replace in the prompt
   * @returns {string} Formatted prompt
   */
  formatPrompt(promptKey, variables = {}) {
    const prompt = this.getPrompt(promptKey);
    if (!prompt) return '';
    
    let formatted = prompt.systemRole;
    
    // Add specific formatting for strategy file upload
    if (promptKey === 'strategyFileUpload' && variables.documentContent) {
      formatted += `

Document Name: ${variables.fileName || 'Unknown'}

Document Content:
${variables.documentContent}

Please extract and structure the strategic framework from this document. Return the result as a JSON object with the following structure:
{
  "vision": "The vision statement",
  "mission": "The mission statement",
  "values": ["Value 1", "Value 2"],
  "pillars": [
    {
      "name": "Pillar name",
      "description": "Pillar description",
      "goals": ["Goal 1", "Goal 2"]
    }
  ],
  "objectives": ["Strategic objective 1", "Strategic objective 2"]
}`;
    }
    
    // Replace any template variables
    Object.keys(variables).forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      formatted = formatted.replace(regex, variables[key]);
    });
    
    return formatted;
  }
  
  /**
   * Get all available prompts
   * @returns {Object} All prompts
   */
  getAllPrompts() {
    return { ...this.prompts };
  }
  
  /**
   * Reset prompts to defaults
   */
  resetToDefaults() {
    this.prompts = { ...this.constructor.prototype.prompts };
    localStorage.removeItem(this.storageKey);
    this.conversationDepth = 0;
    EventBus.emit('prompts:reset');
  }
  
  /**
   * Get service status
   * @returns {Object} Status information
   */
  getStatus() {
    return {
      initialized: this.initialized,
      totalPrompts: Object.keys(this.prompts).length,
      conversationDepth: this.conversationDepth,
      hasCustomPrompts: Object.values(this.prompts).some(p => p.customized)
    };
  }

  /**
   * Run InitiativeExtractor to produce structured JSON for Impact Mapping review.
   * Uses sidecar when configured; falls back to local heuristic.
   * @param {{title?:string, description?:string, objective?:string, key_results?:string[], krs?:string[], owner?:string}} input
   * @returns {Promise<{objectives:any[], key_results:any[], stakeholders:any[], impacts:any[], links:any[]}>}
   */
  async runInitiativeExtractor(input) {
    try {
      if (Config?.CAUSALITY_API_URL) {
        const res = await fetch(`${Config.CAUSALITY_API_URL}/ai/initiative-extractor`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${Config.API_KEY || ''}` },
          body: JSON.stringify({ input })
        });
        const json = await res.json();
        // Best-effort validation if zod available
        try {
          const { AiSchemas } = await import('../schemas/ai.js');
          const parsed = AiSchemas.initiativeExtractor.safeParse(json);
          if (parsed.success) return parsed.data;
        } catch (_) {}
        return json;
      }
    } catch (_) {}
    // Local heuristic fallback
    const title = (input?.title || '').trim();
    const description = (input?.description || '').trim();
    const objective = (input?.objective || '').trim();
    const krs = Array.isArray(input?.key_results) ? input.key_results : Array.isArray(input?.krs) ? input.krs : [];
    const stakeholdersIn = Array.isArray(input?.stakeholders) ? input.stakeholders.filter(Boolean) : [];
    return {
      objectives: objective ? [{ title: objective, description: '' }] : [],
      key_results: (krs || []).map(t => ({ title: String(t) })),
      stakeholders: stakeholdersIn.map(name => ({ name: String(name), type: 'user', segment: '' })),
      impacts: description ? description.split(/[.;\n]/).map(s => s.trim()).filter(Boolean).slice(0, 3).map(t => ({ title: t })) : [],
      links: (krs || []).map(t => ({ from: title || 'initiative', to: String(t), type: 'drives', confidence: 0.5 }))
    };
  }
}

// Create singleton instance
const promptService = new PromptService();

// Export both class and instance
export { PromptService, promptService as default };

// Attach to window in development
if (import.meta.env.DEV) {
  window.PromptService = promptService;
  console.log('🔧 PromptService available at window.PromptService');
  console.log('💡 Check prompts: window.PromptService.getAllPrompts()');
}
// @ts-check
